import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import { useRsColors } from '@/hooks/useRsColors';
import useChartWindow from '@/hooks/useChartWindow';
import ChartRangeSlider from '@/components/charts/ChartRangeSlider';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';
import { isPastThreeYearMark } from '@/utils/chartAxisUtils';
import { isDemoOnPhone } from '@/demo/demoDataWindow';

ChartJS.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Title, Tooltip, Legend);

const DEMO_MONTHS_SHOWN = 3;

/**
 * Where the chart opens in the demo on a phone: the last 3 months, counted back from
 * the newest point so it stays 3 months as the data moves on. Null everywhere else,
 * which leaves the normal start-of-2023 view alone.
 */
function demoThreeMonthCutoff(labels: string[]): number | null {
  if (!isDemoOnPhone()) return null;
  const newest = new Date(labels[labels.length - 1]);
  if (isNaN(newest.getTime())) return null;
  newest.setMonth(newest.getMonth() - DEMO_MONTHS_SHOWN);
  return newest.getTime();
}

export interface AlignedDataset {
  label: string;
  data: (number | null)[];
  color: string;
}

export interface FeedstockLineChartProps {
  title: string;
  unit: string;
  labels: string[];
  alignedChartDatasets: AlignedDataset[];
} 

const FeedstockLineChart: React.FC<FeedstockLineChartProps> = ({
  title,
  unit,
  labels,
  alignedChartDatasets,
}) => {
  const rs = useRsColors();

  const defaultStartIdx = useMemo(() => {
    if (!labels.length) return 0;
    const target = demoThreeMonthCutoff(labels) ?? new Date('2023-01-01').getTime();
    const idx = labels.findIndex((l) => {
      const d = new Date(l);
      return !isNaN(d.getTime()) && d.getTime() >= target;
    });
    return idx >= 0 ? idx : 0;
  }, [labels]);

  const { startIndex, endIndex, setWindow, hasWindowing } = useChartWindow(labels.length, {
    defaultStartIndex: defaultStartIdx,
    defaultEndIndex: Math.max(labels.length - 1, 0),
  });

  const visibleLabels = useMemo(
    () => labels.slice(startIndex, endIndex + 1),
    [labels, startIndex, endIndex],
  );

  const visibleSeries = useMemo(
    () => alignedChartDatasets.map((s) => ({
      ...s,
      data: s.data.slice(startIndex, endIndex + 1),
    })),
    [alignedChartDatasets, startIndex, endIndex],
  );

  const pastThreeYears = useMemo(() => isPastThreeYearMark(visibleLabels), [visibleLabels]);

  const options: any = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: visibleSeries.length > 1, labels: { color: rs.offWhite || '#f3f4f6' } },
      datalabels: { display: false },
      tooltip: {
        backgroundColor: rs.offBlack || '#1a1a1a',
        titleColor: rs.offWhite || '#f3f4f6',
        bodyColor: rs.offWhite || '#f3f4f6',
        borderColor: rs.jetLight || '#3d3d3d',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        type: 'time' as const,
        // <3yr window: every 6 months (MMM yyyy). Once dragged past the
        // 3-year mark, the whole axis switches to year-only.
        time: pastThreeYears
          ? {
              unit: 'year' as const,
              stepSize: 1,
              displayFormats: { year: 'yyyy' },
              tooltipFormat: 'MMM d, yyyy',
            }
          : {
              unit: 'month' as const,
              stepSize: 6,
              displayFormats: { month: 'MMM yyyy' },
              tooltipFormat: 'MMM d, yyyy',
            },
        grid: { color: rs.gridLine, drawTicks: false },
        border: { display: false },
        ticks: {
          color: rs.offWhite,
          font: { size: 12 },
          maxRotation: 0,
          source: 'auto' as const,
        },
      },
      y: {
        ticks: {
          color: rs.offWhite,
          callback: function (value: any, index: number) {
            if (index % 2 === 0) {
              return Number(value).toFixed(2);
            }
          },
        },
        grid: { color: rs.gridLine },
        title: { display: !!unit && unit !== 'mixed', text: unit, color: rs.offWhite },
      },
    },
  }), [rs, unit, visibleSeries.length, pastThreeYears]);

  const data = useMemo(() => ({
    labels: visibleLabels,
    datasets: visibleSeries.map((s) => ({
      label: s.label,
      data: s.data,
      borderColor: s.color,
      backgroundColor: 'transparent',
      pointRadius: 0,
      borderWidth: 2,
      tension: 0.3,
      spanGaps: true,
    })),
  }), [visibleLabels, visibleSeries]);

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs]);

  return (
    <div className="bg-rs-off-black/60 border border-rs-jet-light/50 rounded-xl p-4">
      <h4 className="text-rs-off-white text-sm font-medium mb-2">{title}</h4>
      <div className="h-[200px]">
        <Line key={rs.offWhite} data={data} options={options} plugins={[hoverLinePlugin]} />
      </div>
      {hasWindowing && (
        <ChartRangeSlider
          totalPoints={labels.length}
          startIndex={startIndex}
          endIndex={endIndex}
          onChange={setWindow}
        />
      )}
      <div className="flex justify-between text-xs text-rs-off-white/50 mt-1 px-2">
        <span>{labels[startIndex] || ''}</span>
        <span>{labels[endIndex] || ''}</span>
      </div>
    </div>
  );
};

export default FeedstockLineChart;
